/**
 * Shared configuration for website form pages.
 */

import { type ReactNode } from "react";
import { type WebsiteCategorizationLabels } from "./WebsiteCategorizationFields";
import { type WebsiteFormData } from "@/utils/websiteFormUtils";

export const WEBSITE_FORM_LIMITS = {
  title: 200,
  description: 500,
  url: 512,
  address: 512,
  zip: 5,
} as const;

export const createEmptyWebsiteFormData = (): WebsiteFormData => ({
  title: "",
  url: "",
  description: "",
  address: "",
  zipCode: "",
  category1Id: null,
  category1Name: "",
  category1Depth: null,
  categoryPathIds: [],
  categoryPathNames: [],
  regionId: null,
  regionName: "",
  regionPathIds: [],
  regionPathNames: [],
});

export type WebsiteFormValidationConfig = {
  ids: {
    title: string;
    url: string;
    description: string;
    address: string;
    zip: string;
    category?: string;
    region?: string;
  };
  required: {
    category: boolean;
    region: boolean;
  };
  errorKeys: {
    requiredFields: string;
    categoryRequired?: string;
    regionRequired?: string;
    titleTooLong: string;
    urlTooLong: string;
    descriptionTooLong: string;
    addressTooLong: string;
    zipTooLong: string;
    urlVerifyFailed: string;
    urlExists: string;
  };
};

export type WebsiteFormFieldsConfig = {
  titleInputId: string;
  urlInputId: string;
  descriptionInputId: string;
  addressInputId: string;
  titleLabel: string;
  titlePlaceholder: string;
  urlLabel: string;
  urlPlaceholder: string;
  urlHint?: string;
  descriptionLabel: string;
  descriptionPlaceholder: string;
  showDescriptionCount?: boolean;
  categoryLabelText?: string;
  categoryFieldId?: string;
  regionFieldId?: string;
  categoryRequiredErrorText?: string;
  regionRequiredErrorText?: string;
  addressLabelText?: string;
  regionLabels?: WebsiteCategorizationLabels;
};

export type WebsiteFormCardConfig = {
  title: string;
  description: string;
  submitLabel: string;
  cancelLabel: string;
  submitIcon?: ReactNode;
  actionsClassName?: string;
};

export type WebsiteFormConfig = {
  card: WebsiteFormCardConfig;
  fields: WebsiteFormFieldsConfig;
  validation: WebsiteFormValidationConfig;
};

export const buildWebsiteSuggestFormConfig = (
  t: (key: string) => string,
  submitIcon?: ReactNode
): WebsiteFormConfig => ({
  card: {
    title: t("websiteSuggest.websiteInfo"),
    description: t("websiteSuggest.websiteInfoDesc"),
    submitLabel: t("websiteSuggest.submitButton"),
    cancelLabel: t("websiteSuggest.cancel"),
    submitIcon,
  },
  fields: {
    titleInputId: "title",
    urlInputId: "url",
    descriptionInputId: "description",
    addressInputId: "address",
    titleLabel: t("websiteSuggest.titleLabel"),
    titlePlaceholder: t("websiteSuggest.titlePlaceholder"),
    urlLabel: t("websiteSuggest.urlLabel"),
    urlPlaceholder: t("websiteSuggest.urlPlaceholder"),
    descriptionLabel: t("websiteSuggest.descriptionLabel"),
    descriptionPlaceholder: t("websiteSuggest.descriptionPlaceholder"),
    showDescriptionCount: true,
  },
  validation: {
    ids: {
      title: "title",
      url: "url",
      description: "description",
      address: "address",
      zip: "zipCode",
    },
    required: {
      category: false,
      region: false,
    },
    errorKeys: {
      requiredFields: "websiteSuggest.fillAllFields",
      titleTooLong: "websiteSuggest.errorTitleTooLong",
      urlTooLong: "websiteSuggest.errorUrlTooLong",
      descriptionTooLong: "websiteSuggest.errorDescriptionTooLong",
      addressTooLong: "websiteSuggest.errorAddressTooLong",
      zipTooLong: "websiteSuggest.errorZipTooLong",
      urlVerifyFailed: "websiteSuggest.errorUrlVerifyFailed",
      urlExists: "websiteSuggest.errorUrlExists",
    },
  },
});

export const buildAddWebsiteFormConfig = (
  t: (key: string) => string,
  submitIcon?: ReactNode
): WebsiteFormConfig => ({
  card: {
    title: t("websiteManagement.addWebsiteTitle"),
    description: t("websiteManagement.addWebsiteDesc"),
    submitLabel: t("websiteManagement.addButton"),
    cancelLabel: t("websiteManagement.cancel"),
    submitIcon,
    actionsClassName: "flex flex-col sm:flex-row gap-3",
  },
  fields: {
    titleInputId: "add-title",
    urlInputId: "add-url",
    descriptionInputId: "add-description",
    addressInputId: "add-address",
    titleLabel: t("websiteManagement.titleLabel"),
    titlePlaceholder: t("websiteManagement.titlePlaceholder"),
    urlLabel: t("websiteManagement.urlLabel"),
    urlPlaceholder: t("websiteManagement.urlPlaceholder"),
    descriptionLabel: t("websiteManagement.descriptionLabel"),
    descriptionPlaceholder: t("websiteManagement.descriptionPlaceholder"),
    showDescriptionCount: true,
    categoryLabelText: t("websiteManagement.mainCategoryLabel"),
    categoryFieldId: "add-category",
    regionFieldId: "add-region",
    categoryRequiredErrorText: t("websiteManagement.categoryRequiredError"),
    regionRequiredErrorText: t("websiteManagement.regionRequiredError"),
    addressLabelText: t("websiteManagement.addressLabel"),
    regionLabels: {
      zipLabel: t("websiteManagement.zipCodeLabel"),
      zipPlaceholder: t("websiteManagement.zipCodePlaceholder"),
      zipHint: t("websiteManagement.zipCodeHint"),
      regionLabel: t("websiteManagement.regionLabel"),
      regionPlaceholder: t("websiteManagement.regionPlaceholder"),
      regionHint: t("websiteManagement.regionHint"),
    },
  },
  validation: {
    ids: {
      title: "add-title",
      url: "add-url",
      description: "add-description",
      address: "add-address",
      zip: "zipCode",
      category: "add-category",
      region: "add-region",
    },
    required: {
      category: true,
      region: true,
    },
    errorKeys: {
      requiredFields: "websiteSuggest.fillAllFields",
      categoryRequired: "websiteManagement.categoryRequiredError",
      regionRequired: "websiteManagement.regionRequiredError",
      titleTooLong: "websiteSuggest.errorTitleTooLong",
      urlTooLong: "websiteSuggest.errorUrlTooLong",
      descriptionTooLong: "websiteSuggest.errorDescriptionTooLong",
      addressTooLong: "websiteSuggest.errorAddressTooLong",
      zipTooLong: "websiteSuggest.errorZipTooLong",
      urlVerifyFailed: "websiteSuggest.errorUrlVerifyFailed",
      urlExists: "websiteSuggest.errorUrlExists",
    },
  },
});
